import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  MagnifyingGlassIcon,
  PlusIcon,
  MinusIcon,
  XMarkIcon,
  DocumentTextIcon,
  ClockIcon,
  ArrowPathIcon
} from '@heroicons/react/24/outline';
import { cn } from '@/lib/utils';
import { Tip } from '../ui/tip';
import { Button } from '../ui/button';
import {
  Renglon,
  Estacion,
  agregarRenglon,
  cambiarCantidad,
  quitarRenglon,
  renglonDeServicio,
  renglonDeProducto,
  totalRenglones,
  fmtDuracion,
  plata,
  duracionSugerida,
  estacionesDeRenglones
} from '../../utils/turnoNuevo';

interface Props {
  servicios: any[];
  productos: any[];
  renglones: Renglon[];
  onRenglones: (r: Renglon[]) => void;
  duracion: number;
  duracionAuto: boolean;
  onDuracion: (min: number, auto: boolean) => void;
  estaciones: Estacion[];
  /** presupuestos aprobados del cliente que todavía no tienen turno */
  presupuestos: any[];
  /** presupuesto aplicado al turno (queda enlazado) */
  presupuesto: any | null;
  cargandoPresupuesto: boolean;
  onUsarPresupuesto: (id: string) => void;
  onQuitarPresupuesto: () => void;
}

const MAX_RESULTADOS = 40;

const esEditable = (t: EventTarget | null) => {
  const el = t as HTMLElement | null;
  if (!el) return false;
  return el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.tagName === 'SELECT' || el.isContentEditable;
};

const fechaCorta = (iso?: string) => {
  if (!iso) return '';
  const d = new Date(iso);
  return isNaN(d.getTime()) ? '' : d.toLocaleDateString('es-AR', { day: '2-digit', month: '2-digit' });
};

// Campos numéricos chicos: @tailwindcss/forms les pone relleno (.5rem .75rem) y el
// navegador les suma las flechitas; en 44 px de ancho el número quedaba recortado.
// Se saca el relleno en cada campo (p-0 / py-0) y las flechitas con esto.
const SIN_FLECHITAS = '[appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none';

/**
 * Bloque "Qué": sugerencia de presupuesto aprobado, alta rápida de renglones
 * (un buscador para servicios y productos), tabla con cantidad, tiempo e
 * importe, duración estimada y estaciones por las que pasa el trabajo.
 */
const RenglonesTurno: React.FC<Props> = ({
  servicios, productos, renglones, onRenglones, duracion, duracionAuto, onDuracion,
  estaciones, presupuestos, presupuesto, cargandoPresupuesto, onUsarPresupuesto, onQuitarPresupuesto
}) => {
  const [q, setQ] = useState('');
  const [abierto, setAbierto] = useState(false);
  const [hl, setHl] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const listaRef = useRef<HTMLDivElement>(null);

  const resultados = useMemo(() => {
    const tokens = q.trim().toLowerCase().split(/\s+/).filter(Boolean);
    const coincide = (x: any) => {
      if (tokens.length === 0) return true;
      const texto = `${x.name || ''} ${x.sku || ''}`.toLowerCase();
      return tokens.every((t) => texto.includes(t));
    };
    const s = servicios.filter(coincide).slice(0, MAX_RESULTADOS);
    const p = productos.filter(coincide).slice(0, MAX_RESULTADOS);
    const flat = [
      ...s.map((d) => ({ kind: 'service' as const, d })),
      ...p.map((d) => ({ kind: 'product' as const, d }))
    ];
    return { s, p, flat };
  }, [q, servicios, productos]);

  useEffect(() => { setHl(0); }, [q]);

  // "." desde cualquier lado enfoca el buscador (como en el formulario anterior)
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== '.' || e.ctrlKey || e.metaKey || e.altKey || esEditable(e.target)) return;
      e.preventDefault();
      inputRef.current?.focus();
      setAbierto(true);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  useEffect(() => {
    const lista = listaRef.current;
    if (!abierto || !lista) return;
    const nodo = lista.querySelector(`[data-idx="${hl}"]`) as HTMLElement | null;
    if (!nodo) return;
    const top = nodo.offsetTop;
    const bottom = top + nodo.offsetHeight;
    if (top < lista.scrollTop) lista.scrollTop = top;
    else if (bottom > lista.scrollTop + lista.clientHeight) lista.scrollTop = bottom - lista.clientHeight;
  }, [hl, abierto]);

  const agregar = (idx: number) => {
    const it = resultados.flat[idx];
    if (!it) return;
    const nuevo = it.kind === 'service' ? renglonDeServicio(it.d) : renglonDeProducto(it.d);
    onRenglones(agregarRenglon(renglones, nuevo));
    setQ('');
    setAbierto(false);
    inputRef.current?.focus();
  };

  const onKeyInput = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (!abierto) setAbierto(true);
      else setHl((h) => Math.min(resultados.flat.length - 1, h + 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHl((h) => Math.max(0, h - 1));
    } else if (e.key === 'Enter') {
      if (!abierto || resultados.flat.length === 0) return;
      e.preventDefault();
      agregar(Math.min(Math.max(hl, 0), resultados.flat.length - 1));
    } else if (e.key === 'Escape') {
      setAbierto(false);
    }
  };

  // Duración: estado local para que cada tecla no dispare la búsqueda de horarios
  const [durInput, setDurInput] = useState(String(duracion));
  useEffect(() => { setDurInput(String(duracion)); }, [duracion]);
  const confirmarDuracion = () => {
    const n = parseInt(durInput, 10);
    if (Number.isFinite(n) && n > 0) onDuracion(n, false);
    else setDurInput(String(duracion));
  };

  const total = totalRenglones(renglones);
  const pasaPor = estacionesDeRenglones(renglones, estaciones);
  const sugeridos = presupuestos.filter((b) => b.id !== presupuesto?.id);

  const renderItem = (it: { kind: 'service' | 'product'; d: any }, idx: number) => {
    const d = it.d;
    const precio = Number(d.salePrice ?? d.sellingPrice ?? 0) || 0;
    const detalle = it.kind === 'service'
      ? `${fmtDuracion(Number(d.duration) || 60)}`
      : [typeof (d.stock ?? d.currentStock) !== 'undefined' ? `stock ${d.stock ?? d.currentStock}` : '', d.location].filter(Boolean).join(' · ');
    return (
      <div
        key={`${it.kind}-${d.id}`}
        data-idx={idx}
        onMouseEnter={() => setHl(idx)}
        onMouseDown={(e) => { e.preventDefault(); agregar(idx); }}
        className={cn(
          'flex cursor-pointer items-center gap-3 border-l-2 px-3 py-1.5 text-sm',
          idx === hl ? 'border-l-primary bg-accent text-accent-foreground' : 'border-l-transparent hover:bg-accent/50'
        )}
      >
        <span className="min-w-0 flex-1 truncate">{d.name}</span>
        <span className="shrink-0 text-xs tabular-nums text-muted-foreground">{plata(precio)}{detalle ? ` · ${detalle}` : ''}</span>
      </div>
    );
  };

  return (
    <div className="space-y-3">
      {/* Presupuesto aplicado o sugerido */}
      {presupuesto ? (
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 rounded-lg border border-primary/30 bg-primary/5 px-3 py-2 text-sm">
          <DocumentTextIcon className="h-4 w-4 shrink-0 text-primary" />
          <span className="min-w-0 flex-1 truncate">
            <span className="font-medium">Presupuesto {presupuesto.budgetNumber || ''}</span>
            <span className="text-muted-foreground"> · {presupuesto.description || 'sin descripción'} · el turno queda enlazado</span>
          </span>
          <button type="button" onClick={onQuitarPresupuesto} className="text-xs text-muted-foreground underline underline-offset-2 hover:text-foreground">
            Quitar
          </button>
        </div>
      ) : sugeridos.length > 0 ? (
        <div className="rounded-lg border border-primary/30 bg-primary/5 px-3 py-2">
          <div className="flex items-center gap-2 text-sm font-medium">
            <DocumentTextIcon className="h-4 w-4 shrink-0 text-primary" />
            {sugeridos.length === 1 ? 'Este cliente tiene un presupuesto aprobado sin turno' : `Este cliente tiene ${sugeridos.length} presupuestos aprobados sin turno`}
          </div>
          <div className="mt-1.5 space-y-1">
            {sugeridos.slice(0, 3).map((b) => (
              <div key={b.id} className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm">
                <span className="min-w-0 flex-1 truncate text-muted-foreground">
                  <span className="font-medium text-foreground">{b.budgetNumber}</span>
                  {b.description ? ` · ${b.description}` : ''} · {plata(Number(b.totalAmount ?? b.total ?? 0))}
                  {fechaCorta(b.updatedAt || b.createdAt) ? ` · ${fechaCorta(b.updatedAt || b.createdAt)}` : ''}
                </span>
                <Button type="button" size="sm" variant="outline" className="h-7" disabled={cargandoPresupuesto} onClick={() => onUsarPresupuesto(b.id)}>
                  Usar presupuesto
                </Button>
              </div>
            ))}
          </div>
        </div>
      ) : null}

      {/* Buscador */}
      <div className="relative">
        <div className="flex h-9 items-center gap-2 rounded-lg border border-input bg-background px-3 focus-within:ring-2 focus-within:ring-ring/40">
          <MagnifyingGlassIcon className="h-4 w-4 shrink-0 text-muted-foreground" />
          <input
            ref={inputRef}
            value={q}
            onChange={(e) => { setQ(e.target.value); setAbierto(true); }}
            onFocus={() => setAbierto(true)}
            onBlur={() => setTimeout(() => setAbierto(false), 150)}
            onKeyDown={onKeyInput}
            placeholder="Agregar servicio o producto… ej: alineación, 265 65 r17, aceite 5w-30"
            aria-label="Agregar servicio o producto"
            className="min-w-0 flex-1 bg-transparent text-sm outline-none placeholder:text-muted-foreground/70"
          />
          <kbd className="hidden rounded border border-border px-1.5 py-px font-mono text-[11px] text-muted-foreground sm:inline">.</kbd>
        </div>
        {abierto && (
          <div ref={listaRef} className="absolute z-40 mt-1 max-h-[300px] w-full overflow-y-auto rounded-lg border border-border bg-popover shadow-lg">
            {resultados.flat.length === 0 ? (
              <div className="px-3 py-4 text-center text-sm text-muted-foreground">Nada con ese nombre en el catálogo</div>
            ) : (
              <>
                {resultados.s.length > 0 && (
                  <div>
                    <div className="bg-muted/40 px-3 py-1 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Servicios</div>
                    {resultados.flat.slice(0, resultados.s.length).map((it, i) => renderItem(it, i))}
                  </div>
                )}
                {resultados.p.length > 0 && (
                  <div>
                    <div className="bg-muted/40 px-3 py-1 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Productos</div>
                    {resultados.flat.slice(resultados.s.length).map((it, i) => renderItem(it, i + resultados.s.length))}
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </div>

      {/* Renglones */}
      {renglones.length === 0 ? (
        <div className="rounded-lg border border-dashed border-border px-3 py-5 text-center text-sm text-muted-foreground">
          Todavía no hay nada cargado. Buscá arriba{sugeridos.length > 0 ? ' o usá el presupuesto' : ''}.
        </div>
      ) : (
        <div className="overflow-hidden rounded-lg border border-border">
          <div className="hidden items-center gap-3 border-b border-border bg-muted/40 px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground sm:flex">
            <span className="min-w-0 flex-1">Renglón</span>
            <span className="w-[104px] text-center">Cant.</span>
            <span className="w-16 text-right">Tiempo</span>
            <span className="w-[96px] text-right">Importe</span>
            <span className="w-7" />
          </div>
          {renglones.map((r, idx) => {
            const incluidos = (r.comboItems || []).filter((c) => c.incluido !== false).map((c) => c.label);
            const combo = incluidos.length
              ? `incluye ${incluidos.slice(0, 3).join(', ')}${incluidos.length > 3 ? ` y ${incluidos.length - 3} más` : ''}`
              : '';
            const meta = r.type === 'service'
              ? ['Servicio', estaciones.find((e) => e.key === r.stationKey)?.name, r.fromBudget ? 'del presupuesto' : '', combo].filter(Boolean).join(' · ')
              : ['Producto', r.location, typeof r.stock !== 'undefined' && r.stock !== null ? `quedan ${r.stock}` : '', r.fromBudget ? 'del presupuesto' : '', combo].filter(Boolean).join(' · ');
            return (
              <div key={`${r.type}-${r.id}-${idx}`} className="flex flex-wrap items-center gap-x-3 gap-y-1.5 border-b border-border px-3 py-2 last:border-b-0">
                <div className="min-w-0 flex-1 basis-[200px]">
                  <div className="truncate text-sm font-medium">{r.name}</div>
                  <div className="truncate text-xs text-muted-foreground">{meta}</div>
                </div>
                <div className="ml-auto flex items-center gap-3">
                  <div className="flex w-[104px] items-center justify-center gap-1">
                    <Tip label="Uno menos">
                      <button type="button" onClick={() => onRenglones(cambiarCantidad(renglones, idx, r.qty - 1))} disabled={r.qty <= 1} className="flex h-7 w-7 items-center justify-center rounded-md border border-border text-muted-foreground hover:bg-accent disabled:opacity-40">
                        <MinusIcon className="h-3.5 w-3.5" />
                      </button>
                    </Tip>
                    <input
                      type="number"
                      min={1}
                      value={r.qty}
                      onChange={(e) => onRenglones(cambiarCantidad(renglones, idx, Number(e.target.value)))}
                      aria-label={`Cantidad de ${r.name}`}
                      className={`h-7 w-11 rounded-md border border-border bg-background p-0 text-center text-sm tabular-nums outline-none focus:ring-2 focus:ring-ring/40 ${SIN_FLECHITAS}`}
                    />
                    <Tip label="Uno más">
                      <button type="button" onClick={() => onRenglones(cambiarCantidad(renglones, idx, r.qty + 1))} className="flex h-7 w-7 items-center justify-center rounded-md border border-border text-muted-foreground hover:bg-accent">
                        <PlusIcon className="h-3.5 w-3.5" />
                      </button>
                    </Tip>
                  </div>
                  <span className="hidden w-16 text-right text-xs tabular-nums text-muted-foreground sm:inline">
                    {r.type === 'service' ? fmtDuracion((Number(r.duration) || 60) * r.qty) : '—'}
                  </span>
                  <span className="w-[96px] text-right text-sm font-medium tabular-nums">{plata(r.sellingPrice * r.qty)}</span>
                  <Tip label="Quitar">
                    <button type="button" onClick={() => onRenglones(quitarRenglon(renglones, idx))} className="flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground hover:bg-accent hover:text-destructive">
                      <XMarkIcon className="h-4 w-4" />
                    </button>
                  </Tip>
                </div>
              </div>
            );
          })}
          <div className="flex flex-wrap items-center gap-x-4 gap-y-2 bg-muted/40 px-3 py-2 text-sm">
            <span className="text-muted-foreground">{renglones.length === 1 ? '1 renglón' : `${renglones.length} renglones`}</span>
            <div className="flex items-center gap-1.5">
              <ClockIcon className="h-4 w-4 text-muted-foreground" />
              <input
                type="number"
                min={15}
                max={1440}
                value={durInput}
                onChange={(e) => setDurInput(e.target.value)}
                onBlur={confirmarDuracion}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); confirmarDuracion(); } }}
                aria-label="Duración estimada en minutos"
                className={`h-7 w-16 rounded-md border border-border bg-background px-1.5 py-0 text-right text-sm tabular-nums outline-none focus:ring-2 focus:ring-ring/40 ${SIN_FLECHITAS}`}
              />
              <span className="text-muted-foreground">min · {fmtDuracion(duracion)}</span>
              {duracionAuto ? (
                <Tip label="Suma de los servicios. Si comparten pasos (balanceo y desarme usan el mismo sacar/poner rueda), ajustala a mano">
                  <span className="rounded border border-border px-1.5 py-px text-[11px] text-muted-foreground">sugerida</span>
                </Tip>
              ) : (
                <Tip label="Volver a la suma de los servicios">
                  <button type="button" onClick={() => onDuracion(duracionSugerida(renglones), true)} className="inline-flex items-center gap-1 text-xs text-muted-foreground underline underline-offset-2 hover:text-foreground">
                    <ArrowPathIcon className="h-3.5 w-3.5" /> recalcular
                  </button>
                </Tip>
              )}
            </div>
            <span className="ml-auto font-semibold tabular-nums">Total {plata(total)}</span>
          </div>
        </div>
      )}

      {pasaPor.length > 0 && (
        <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
          Pasa por
          {pasaPor.map((e) => (
            <span key={e.key} className="inline-flex items-center gap-1.5 rounded-full border border-border bg-muted/40 px-2 py-0.5 text-foreground">
              <span className="h-1.5 w-1.5 rounded-full bg-current" />{e.name}
            </span>
          ))}
          <span>· el orden lo define el taller al recepcionar</span>
        </div>
      )}
    </div>
  );
};

export default RenglonesTurno;
